import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
} from "@mui/material";
import { SERVER_URL } from "../../constants";

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Create a bespoke EVENT product (free-form name + one price per head) and hand the
// caller its new productId to book. The booking itself is caller-specific:
// an individual books one student; a group batch-books all members. Reused by both.
// A single number per head; not commissionable by default (pass-through extra).
export default function BespokeEventDialog({
  open,
  onClose,
  defaultCampus = "",
  defaultDate,
  windowStart = "", // optional "YYYY-MM-DD" — warn if the event date falls outside
  windowEnd = "",   // the [windowStart, windowEnd] window (e.g. the group stay dates)
  onBook, // async ({ productId, productName, perHead }) => void
}) {
  const [name, setName] = useState("");
  const [perHead, setPerHead] = useState("");
  const [date, setDate] = useState(defaultDate || todayStr());
  const [campus, setCampus] = useState(defaultCampus || "");
  const [commissionable, setCommissionable] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setPerHead("");
    setDate(defaultDate || todayStr());
    setCampus(defaultCampus || "");
    setCommissionable(false);
    setSaving(false);
    setError(null);

    const token = sessionStorage.getItem("bearer");
    fetch(`${SERVER_URL}api/campuses/active`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCampuses(Array.isArray(data) ? data : []))
      .catch(() => setCampuses([]));
  }, [open, defaultCampus, defaultDate]);

  const price = Number(perHead);
  const canSave =
    name.trim() !== "" &&
    Number.isFinite(price) &&
    price > 0 &&
    Boolean(date) &&
    Boolean(campus) &&
    !saving;

  const outOfWindow =
    Boolean(windowStart && windowEnd && date) &&
    (date < windowStart || date > windowEnd);

  const handleSave = async () => {
    if (!canSave) return;
    if (
      outOfWindow &&
      !window.confirm(
        `The event date ${date} is outside the group stay (${windowStart} – ${windowEnd}). Create it anyway?`
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    const token = sessionStorage.getItem("bearer");
    try {
      const payload = {
        name: name.trim(),
        base: campus,
        defaultRate: price,
        startDate: date,
        endDate: date,
        capacity: 500,
        allocated: 0,
        commissionable: !!commissionable,
        productType: "EVENT",
        notes: "",
        residential: true,
      };

      const res = await fetch(`${SERVER_URL}api/products`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to create the event product (${res.status})`);
      }
      const created = await res.json().catch(() => null);
      const productId = created?.id ?? created?.productId;
      if (productId == null) {
        throw new Error("Event product was created but the server returned no id to book onto.");
      }

      await onBook?.({ productId, productName: name.trim(), perHead: price });
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  // Make sure the default campus is selectable even before the list loads.
  const campusNames = Array.from(
    new Set([
      ...(campus ? [campus] : []),
      ...campuses.map((c) => c.campusName || c.name).filter(Boolean),
    ])
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add bespoke event</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Creates a one-off EVENT product and books it at one price per head. The price
            should cover everyone going (campers, staff, transport) — it isn&apos;t broken out.
          </Typography>

          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          <TextField
            label="Event name"
            placeholder="e.g. Castle trip"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="Price per head (£)"
            type="number"
            value={perHead}
            onChange={(e) => setPerHead(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
          />
          <TextField
            label="Event date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {outOfWindow ? (
            <Alert severity="warning">
              {date} is outside the group stay dates ({windowStart} – {windowEnd}). Check
              the date is right — you&apos;ll be asked to confirm.
            </Alert>
          ) : null}
          <FormControl size="small" fullWidth>
            <InputLabel id="bespoke-campus">Campus</InputLabel>
            <Select
              labelId="bespoke-campus"
              label="Campus"
              value={campus}
              onChange={(e) => setCampus(e.target.value)}
            >
              {campusNames.length === 0 ? (
                <MenuItem value="" disabled>
                  No campuses
                </MenuItem>
              ) : (
                campusNames.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={commissionable}
                onChange={(e) => setCommissionable(e.target.checked)}
              />
            }
            label="Commissionable (agents earn commission on it)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? "Saving…" : "Create & book"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
